/**
 * The client's own view of their MOU, and where they sign it — no login.
 *
 * Reached at /mou/:slug/:token. The tenant slug is in the path because the
 * backend has no JWT to resolve a database from on this route; the token is
 * what authorises the read, and it is looked up inside that tenant's DB.
 *
 * This is a page somebody is asked to commit money on, usually on a phone from
 * a WhatsApp message, so it is built to be read before it is signed: the
 * commercials sit above the fold as three plain figures, the scope and terms
 * are next, and the signature block is last and cannot be reached without
 * scrolling past what is being agreed to. The confirm checkbox is unticked and
 * the name has to be typed — a single "Accept" button on a page nobody read is
 * not consent.
 *
 * The signature is drawn (or the typed name stands as one), and when the
 * project requires it, a one-time code emailed to the address the MOU was sent
 * to has to be entered first — proof the signer controls that mailbox, not
 * just that they hold a forwarded link.
 *
 * Tailwind's darkMode is 'class' and this route renders outside the app shell
 * that sets it, so the class goes on this page's own root, driven by
 * prefers-color-scheme. Scoped deliberately: putting it on documentElement
 * would flip the whole CRM's theme.
 */

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  AlertCircle, BadgeCheck, CalendarClock, Check, Download, FileSignature,
  FileText, IndianRupee, Loader2, Mail, MapPin, PenLine, ShieldCheck, Type,
} from "lucide-react";

import SignaturePad from "../../components/common/SignaturePad";
import {
  API_URL, MOU_PUBLIC_DOCUMENT_URL, MOU_PUBLIC_OTP_URL, MOU_PUBLIC_SIGN_URL, MOU_PUBLIC_URL,
} from "../../constants/api";
import { DOC_CLASSES, withLayoutMarkers } from "./docStyles";

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : null;

const fmtDateTime = (d) =>
  d ? new Date(d).toLocaleString("en-IN", {
        day: "numeric", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      })
    : null;

const fmtMoney = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? `₹${n.toLocaleString("en-IN")}` : "—";
};

export default function MOUPublic() {
  const { slug, token } = useParams();

  const [data, setData]       = useState(null);
  const [error, setError]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [dark, setDark]       = useState(false);

  const [name, setName]         = useState("");
  const [desig, setDesig]       = useState("");
  const [agreed, setAgreed]     = useState(false);
  const [signing, setSigning]   = useState(false);
  const [signErr, setSignErr]   = useState("");
  const [done, setDone]         = useState(null);

  const [sigMode, setSigMode]   = useState("draw");   // draw | type
  const [sigImage, setSigImage] = useState("");
  const [otp, setOtp]           = useState("");
  const [otpState, setOtpState] = useState({ sent: false, busy: false, msg: "" });

  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mq) return;
    setDark(mq.matches);
    const onChange = (e) => setDark(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  useEffect(() => {
    axios.get(`${API_URL}${MOU_PUBLIC_URL(slug, token)}`)
      .then((r) => {
        if (r.data?.status === "success") setData(r.data.data);
        else setError(r.data?.message || "This MOU link is not valid");
      })
      .catch((e) => setError(e.response?.data?.message || "This MOU link is not valid"))
      .finally(() => setLoading(false));
  }, [slug, token]);

  const requestOtp = async () => {
    setOtpState({ sent: otpState.sent, busy: true, msg: "" });
    try {
      const r = await axios.post(`${API_URL}${MOU_PUBLIC_OTP_URL(slug, token)}`, {});
      setOtpState({ sent: true, busy: false, msg: r.data?.message || "Code sent." });
    } catch (e) {
      setOtpState({ sent: otpState.sent, busy: false,
                    msg: e.response?.data?.message || "The code could not be sent." });
    }
  };

  const sign = async () => {
    setSignErr("");
    if (!name.trim()) { setSignErr("Please type your full name."); return; }
    if (sigMode === "draw" && !sigImage) { setSignErr("Please draw your signature in the box."); return; }
    if (data.requires_otp && !/^\d{6}$/.test(otp.trim())) {
      setSignErr("Enter the 6-digit code we emailed you."); return;
    }
    if (!agreed)      { setSignErr("Please confirm you agree to the terms."); return; }
    setSigning(true);
    try {
      const r = await axios.post(`${API_URL}${MOU_PUBLIC_SIGN_URL(slug, token)}`, {
        name, designation: desig, agreed: true,
        signature_type: sigMode === "draw" ? "drawn" : "typed",
        signature_image: sigMode === "draw" ? sigImage : "",
        otp: otp.trim(),
      });
      if (r.data?.status === "success") setDone(r.data.data || {});
      else setSignErr(r.data?.message || "Your signature could not be recorded.");
    } catch (e) {
      setSignErr(e.response?.data?.message || "Your signature could not be recorded.");
    } finally {
      setSigning(false);
    }
  };

  const shell = (children) => (
    <div className={dark ? "dark" : ""}>
      <div className="min-h-screen bg-slate-100 font-sans antialiased text-slate-900 dark:bg-slate-950 dark:text-slate-100">
        {children}
      </div>
    </div>
  );

  if (loading) {
    return shell(
      <div className="grid min-h-screen place-items-center">
        <div className="flex flex-col items-center gap-3 text-indigo-500">
          <Loader2 className="h-6 w-6 animate-spin" />
          <p className="text-sm text-slate-500 dark:text-slate-400">Loading your agreement…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return shell(
      <div className="grid min-h-screen place-items-center px-4">
        <div className="max-w-sm rounded-2xl bg-white p-8 text-center shadow-sm dark:bg-slate-900">
          <AlertCircle className="mx-auto h-8 w-8 text-amber-500" />
          <p className="mt-3 text-sm font-medium">{error}</p>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            The link may have been revoked or replaced. Please ask for a fresh one.
          </p>
        </div>
      </div>
    );
  }

  // Signed, either just now or before this page was opened.
  const signedAt   = done?.signed_at || data.signed_at;
  const signedName = done?.signed_by_name || data.signed_by_name;
  const isSigned   = Boolean(done) || data.mou_status === "signed";

  return shell(
    <div className="mx-auto max-w-2xl px-4 py-6 sm:py-10">

      {/* ── header band ────────────────────────────────────────────── */}
      <div className="overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 to-indigo-800 p-6 text-white shadow-lg">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wide text-indigo-200">
              {data.provider?.name || "Agreement"}
            </p>
            <h1 className="mt-1 truncate text-xl font-semibold">{data.title}</h1>
            <p className="mt-1 text-sm text-indigo-100">{data.mou_number}</p>
          </div>
          <FileSignature className="h-8 w-8 shrink-0 text-indigo-200" />
        </div>
        <div className="mt-4 border-t border-white/20 pt-3">
          <p className="text-sm font-medium">{data.org_name}</p>
          {[data.city, data.state].filter(Boolean).length > 0 && (
            <p className="mt-0.5 flex items-center gap-1 text-xs text-indigo-100">
              <MapPin className="h-3 w-3" />
              {[data.city, data.state].filter(Boolean).join(", ")}
            </p>
          )}
        </div>
      </div>

      {/* ── what is being agreed, as figures ───────────────────────── */}
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="rounded-xl bg-white p-4 shadow-sm dark:bg-slate-900">
          <p className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-slate-400">
            <IndianRupee className="h-3 w-3" /> Value
          </p>
          <p className="mt-1 text-lg font-semibold">{fmtMoney(data.contract_value)}</p>
        </div>
        <div className="rounded-xl bg-white p-4 shadow-sm dark:bg-slate-900">
          <p className="text-[11px] uppercase tracking-wide text-slate-400">Period</p>
          <p className="mt-1 text-sm font-medium">{data.contract_period || "—"}</p>
        </div>
        <div className="rounded-xl bg-white p-4 shadow-sm dark:bg-slate-900">
          <p className="text-[11px] uppercase tracking-wide text-slate-400">Payment terms</p>
          <p className="mt-1 text-sm font-medium">{data.payment_terms || "—"}</p>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl bg-white px-4 py-3 text-xs text-slate-600 shadow-sm dark:bg-slate-900 dark:text-slate-300">
        {data.effective_date && (
          <span className="flex items-center gap-1">
            <CalendarClock className="h-3.5 w-3.5 text-slate-400" />
            Effective {fmtDate(data.effective_date)}
          </span>
        )}
        {data.valid_until && (
          <span className={data.expired ? "font-medium text-red-600 dark:text-red-400" : ""}>
            {data.expired ? "Expired on " : "Valid until "}{fmtDate(data.valid_until)}
          </span>
        )}
        {data.has_document && (
          <a
            className="ml-auto flex items-center gap-1 font-medium text-indigo-600 hover:underline dark:text-indigo-400"
            href={`${API_URL}${MOU_PUBLIC_DOCUMENT_URL(slug, token)}`}
          >
            <Download className="h-3.5 w-3.5" />
            {data.document_name || "Download the MOU"}
          </a>
        )}
      </div>

      {/* ── the full agreement, when one was generated from a template ── */}
      {data.body_html ? (
        <div className="mt-4 rounded-xl bg-white p-5 shadow-sm dark:bg-slate-900 sm:p-7">
          <h2 className="mb-3 flex items-center gap-1.5 text-sm font-semibold">
            <FileText className="h-4 w-4 text-indigo-500" /> The agreement
          </h2>
          {/* Sanitised on the server when it was written (releases/sanitize.py). */}
          <div className={DOC_CLASSES} dangerouslySetInnerHTML={{ __html: withLayoutMarkers(data.body_html) }} />
        </div>
      ) : null}

      {/* ── scope and terms ────────────────────────────────────────── */}
      {!data.body_html && data.scope_summary && (
        <div className="mt-4 rounded-xl bg-white p-5 shadow-sm dark:bg-slate-900">
          <h2 className="text-sm font-semibold">Scope</h2>
          <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-slate-700 dark:text-slate-300">
            {data.scope_summary}
          </p>
        </div>
      )}
      {!data.body_html && data.terms_text && (
        <div className="mt-3 rounded-xl bg-white p-5 shadow-sm dark:bg-slate-900">
          <h2 className="text-sm font-semibold">Terms</h2>
          <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-slate-700 dark:text-slate-300">
            {data.terms_text}
          </p>
        </div>
      )}

      {/* ── the signature block ────────────────────────────────────── */}
      <div className="mt-4 rounded-xl bg-white p-5 shadow-sm dark:bg-slate-900">
        {isSigned ? (
          <div className="text-center">
            <BadgeCheck className="mx-auto h-10 w-10 text-emerald-500" />
            <p className="mt-3 text-sm font-semibold text-emerald-700 dark:text-emerald-300">
              This agreement has been signed
            </p>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              {signedName ? `Signed by ${signedName}` : "Signed"}
              {signedAt ? ` on ${fmtDateTime(signedAt)}` : ""}
            </p>
            {done && (
              <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                Thank you. {data.provider?.name || "Our team"} will be in touch to begin
                onboarding — you do not need to do anything else here.
              </p>
            )}
          </div>
        ) : data.expired ? (
          <div className="text-center">
            <AlertCircle className="mx-auto h-8 w-8 text-amber-500" />
            <p className="mt-2 text-sm font-medium">This agreement has expired</p>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              It was valid until {fmtDate(data.valid_until)}. Please ask{" "}
              {data.provider?.name || "us"} for a fresh copy.
            </p>
          </div>
        ) : !data.can_sign ? (
          <div className="text-center">
            <AlertCircle className="mx-auto h-8 w-8 text-slate-400" />
            <p className="mt-2 text-sm font-medium">
              This agreement is not open for signing ({data.status_label})
            </p>
          </div>
        ) : (
          <>
            <h2 className="flex items-center gap-1.5 text-sm font-semibold">
              <ShieldCheck className="h-4 w-4 text-indigo-500" />
              Sign this agreement
            </h2>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Your signature records your agreement to the terms above, along with the date,
              time, browser and network address you sign from. A signed copy is emailed to you.
            </p>

            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <label className="text-xs font-medium">
                  Your full name <span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-indigo-500 dark:border-slate-700 dark:bg-slate-800"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Dr A. Kumar"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium">Your designation</label>
                <input
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-indigo-500 dark:border-slate-700 dark:bg-slate-800"
                  value={desig}
                  onChange={(e) => setDesig(e.target.value)}
                  placeholder="Managing Director"
                />
              </div>
            </div>

            {/* ── draw or type ─────────────────────────────────── */}
            <div className="mt-4">
              <div className="mb-2 inline-flex rounded-lg bg-slate-100 p-0.5 text-xs dark:bg-slate-800">
                {[
                  { k: "draw", label: "Draw signature", icon: PenLine },
                  { k: "type", label: "Type name", icon: Type },
                ].map(({ k, label, icon: Icon }) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setSigMode(k)}
                    className={`flex items-center gap-1 rounded-md px-3 py-1.5 font-medium transition-colors ${
                      sigMode === k
                        ? "bg-white text-indigo-700 shadow-sm dark:bg-slate-700 dark:text-indigo-300"
                        : "text-slate-500 dark:text-slate-400"
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5" /> {label}
                  </button>
                ))}
              </div>
              {sigMode === "draw" ? (
                <SignaturePad onChange={setSigImage} dark={dark} />
              ) : (
                <div className="flex h-24 items-center justify-center rounded-lg border-2 border-dashed border-slate-300 bg-white px-4 dark:border-slate-600">
                  <span className="truncate font-serif text-3xl italic text-blue-900">
                    {name.trim() || <span className="font-sans text-xs not-italic text-slate-400">Your typed name appears here</span>}
                  </span>
                </div>
              )}
            </div>

            {/* ── email verification ───────────────────────────── */}
            {data.requires_otp && (
              <div className="mt-4 rounded-lg border border-indigo-200 bg-indigo-50/60 p-3 dark:border-indigo-900/50 dark:bg-indigo-950/30">
                <p className="flex items-center gap-1.5 text-xs font-medium text-indigo-900 dark:text-indigo-200">
                  <Mail className="h-3.5 w-3.5" /> Verify your email
                </p>
                <p className="mt-1 text-[11px] text-indigo-800/80 dark:text-indigo-200/80">
                  We will email a 6-digit code to <strong>{data.otp_email}</strong>, the address
                  this agreement was sent to.
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={requestOtp}
                    disabled={otpState.busy}
                    className="rounded-lg border border-indigo-300 bg-white px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-60 dark:border-indigo-700 dark:bg-slate-900 dark:text-indigo-300"
                  >
                    {otpState.busy ? "Sending…" : otpState.sent ? "Send again" : "Email me a code"}
                  </button>
                  <input
                    inputMode="numeric"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                    placeholder="6-digit code"
                    className="w-32 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-center text-sm tracking-[0.3em] outline-none focus:border-indigo-500 dark:border-slate-700 dark:bg-slate-800"
                  />
                </div>
                {otpState.msg && (
                  <p className="mt-1.5 text-[11px] text-indigo-800 dark:text-indigo-200">{otpState.msg}</p>
                )}
              </div>
            )}

            <label className="mt-4 flex cursor-pointer items-start gap-2 text-xs text-slate-700 dark:text-slate-300">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-indigo-600"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
              />
              <span>
                I have read the scope and terms above and I agree to them on behalf of{" "}
                <strong>{data.org_name}</strong>.
              </span>
            </label>

            {signErr && (
              <p className="mt-3 flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
                <AlertCircle className="h-3.5 w-3.5" /> {signErr}
              </p>
            )}

            <button
              onClick={sign}
              disabled={signing}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-indigo-700 disabled:opacity-60"
            >
              {signing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Agree and sign
            </button>
          </>
        )}
      </div>

      <p className="mt-6 text-center text-[11px] text-slate-400">
        Sent to you by {data.provider?.name || "our team"}. Do not forward this link — anyone who
        opens it can read and sign this agreement.
      </p>
    </div>
  );
}
